/**
 * ENG-3353 — tell the deny list it changed, from outside its own hook.
 *
 * Two writes now change what the deny list shows without going through `useExtensionBlacklist`:
 * approving a request can take an extension off the deny list, and revoking an exception changes the
 * "N members excepted" count beside a deny. The Extensions page owns that hook; the approve button
 * lives in a section the layout feeds, and the exceptions in the deny section itself. Threading a
 * refetch callback through both would mean every future writer has to remember to ask for one — the
 * same reasoning as `notifyOrgPolicyChanged`, which this mirrors for the page rather than the
 * extension.
 *
 * In-page and synchronous. Listeners are the hooks that reload; a throwing listener must not stop the
 * others, or fail the write that announced the change.
 */
type Listener = () => void;

const listeners = new Set<Listener>();

/** Subscribe; returns the unsubscribe, so it can be an effect's cleanup as it stands. */
export function onDenyListChanged(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Call after a write that changed the deny list or its exceptions. Never throws. */
export function notifyDenyListChanged(): void {
  listeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // One stale view must not stop the others refreshing, or surface as a failed save.
    }
  });
}
